import { motion } from "framer-motion";
import { Home, CookingPot, Zap, Paintbrush, DoorOpen, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const SERVICES = [
  {
    icon: Home,
    title: "Rénovation complète",
    text: "Rénovation de maisons et d’appartements, comprenant l’aménagement des espaces, les installations, les revêtements et les finitions.",
  },
  {
    icon: CookingPot,
    title: "Cuisines et salles de bains",
    text: "Rénovation de cuisines et de salles de bains pour créer des espaces modernes, confortables et fonctionnels.",
  },
  {
    icon: Zap,
    title: "Électricité et plomberie",
    text: "Rénovation et adaptation des installations électriques et sanitaires nécessaires à chaque projet.",
  },
  {
    icon: Paintbrush,
    title: "Peinture et revêtements",
    text: "Travaux de peinture, pose de sols, carrelage et revêtements intérieurs.",
  },
  {
    icon: DoorOpen,
    title: "Menuiserie et finitions",
    text: "Installation de portes, d’armoires et d’autres éléments permettant de finaliser la rénovation avec des finitions soignées.",
  },
];

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="bg-white">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 max-w-2xl"
        >
          <p className="mb-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.28em] text-brand">
            <span className="font-display text-sm tracking-normal text-slate-400">02</span>
            <span className="h-px w-8 bg-sun" aria-hidden="true" />
            Ce que nous réalisons
          </p>
          <h2
            data-testid="services-title"
            className="font-display text-3xl font-normal tracking-tight text-ink sm:text-4xl"
          >
            Nos services
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((service, i) => {
            const Icon = service.icon;
            return (
              <motion.article
                key={service.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`service-card-${i + 1}`}
                className="group relative flex flex-col rounded-sm border border-slate-200 bg-white p-8 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-[0_18px_45px_rgba(15,23,42,0.08)]"
              >
                <div className="mb-8 flex items-start justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-brand-light text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                    <Icon className="h-5 w-5" strokeWidth={1.6} />
                  </span>
                  <span className="font-display text-sm text-slate-300 transition-colors duration-300 group-hover:text-brand">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-display text-xl font-medium tracking-tight text-ink">
                  {service.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                  {service.text}
                </p>
              </motion.article>
            );
          })}

          <motion.button
            type="button"
            onClick={() => scrollToId("devis")}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="services-cta-card"
            className="group flex flex-col justify-between rounded-sm bg-brand p-8 text-left transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-sun"
          >
            <span className="font-display text-2xl font-light leading-snug text-white transition-colors duration-300 group-hover:text-ink">
              Un projet de rénovation à Genève&nbsp;?
            </span>
            <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-white/90 transition-colors duration-300 group-hover:text-ink">
              Parlons-en
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2} />
            </span>
          </motion.button>
        </div>
      </div>
    </section>
  );
};
